import { EventInput } from '@fullcalendar/core';
import { CalendarEventType } from '../../../api-service/clients';
import {
  CITIZEN_LESS_EVENT_COLOR,
  DARK_TEXT_COLOR,
  EDITABLE_EVENT_COLOR,
  LIGHT_TEXT_COLOR,
  READONLY_EVENT_COLOR,
  parseEventDate,
} from './calendar-event-display';
import { ServiceProviderCalendarEventInputModel } from './service-provider-calendar-event-model';

function resolveBackgroundColor(
  event: ServiceProviderCalendarEventInputModel
): string {
  if (!event.citizenId) {
    return CITIZEN_LESS_EVENT_COLOR;
  }

  return event.canEdit ? EDITABLE_EVENT_COLOR : READONLY_EVENT_COLOR;
}

function resolveTextColor(
  event: ServiceProviderCalendarEventInputModel
): string {
  return event.citizenId ? LIGHT_TEXT_COLOR : DARK_TEXT_COLOR;
}

/**
 * Format a Date as a local ISO datetime string without timezone offset
 * (e.g. "2026-06-03T10:30:00"). FullCalendar's rrule plugin interprets
 * a bare datetime (no trailing Z) in the calendar's local timezone,
 * which is what we want. A trailing Z would shift the dtstart to UTC and
 * displace occurrences in other timezones.
 */
function toLocalIsoString(date: Date): string {
  function pad(n: number): string {
    return String(n).padStart(2, '0');
  }
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

export function mapServiceProviderCalendarEvent(
  event: ServiceProviderCalendarEventInputModel
): EventInput | null {
  const startDate = parseEventDate(event.startDate);
  const endDate = parseEventDate(event.endDate);
  if (!startDate || !endDate || !event.entityId) {
    return null;
  }

  const backgroundColor = resolveBackgroundColor(event);
  const textColor = resolveTextColor(event);

  const sharedProps: EventInput = {
    id: event.entityId,
    title: event.title,
    allDay: event.allDay,
    backgroundColor,
    borderColor: backgroundColor,
    textColor,
    extendedProps: {
      ...event,
      type: CalendarEventType.Event,
      eventId: event.entityId,
      partialObjectiveId: event.partialObjectiveId,
      observationTypeId: event.observationTypeId,
      observationTypeName: event.observationTypeName,
      citizenId: event.citizenId,
      serviceProviderUnitId: event.serviceProviderUnitId,
      seriesId: event.seriesId,
    },
  };

  if (event.recurrence) {
    // Recurring event: rrule plugin expands occurrences client-side.
    // dtstart is injected here (not part of the recurrence object per contract).
    // duration uses HH:mm string format which FullCalendar's rrule plugin accepts.
    const durationMs = endDate.getTime() - startDate.getTime();
    const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
    const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const duration = `${String(durationHours).padStart(2, '0')}:${String(durationMinutes).padStart(2, '0')}`;

    // Only forward fields that are actually set. The BFF serializes unset optional
    // fields as explicit `null` (e.g. interval is null for "every day"), and rrule.js
    // rejects null options like `interval: null` — silently expanding no occurrences.
    const { freq, interval, byweekday, bymonthday, count, until } = event.recurrence;
    const rrule = {
      freq,
      dtstart: toLocalIsoString(startDate),
      ...(interval != null ? { interval } : {}),
      ...(byweekday != null && byweekday.length > 0 ? { byweekday } : {}),
      ...(bymonthday != null ? { bymonthday } : {}),
      ...(count != null ? { count } : {}),
      ...(until != null ? { until } : {}),
    };

    // excludedDates is now part of the generated RecurrencePatternModel.
    // The generator emits `Date | string[] | undefined` for DateTime[]; at runtime
    // the BFF always sends string[] (ISO datetimes), so we read it as such.
    const excludedDates = event.recurrence.excludedDates as string[] | undefined;
    const exdate =
      excludedDates && excludedDates.length > 0
        ? excludedDates.map(d => toLocalIsoString(new Date(d)))
        : undefined;

    const isEditable = event.canEdit;
    return {
      ...sharedProps,
      rrule,
      duration,
      ...(exdate ? { exdate } : {}),
      // Allow dragging and resizing individual occurrences; on release the scope chooser
      // lets the user apply the change to this occurrence only or the whole series.
      editable: isEditable,
      startEditable: isEditable,
      durationEditable: isEditable,
      resizable: isEditable,
      draggable: isEditable,
    };
  }

  // One-off event: unchanged behavior.
  const isEditable = event.canEdit;
  return {
    ...sharedProps,
    start: startDate,
    end: endDate,
    editable: isEditable,
    draggable: isEditable,
    startEditable: isEditable,
    durationEditable: isEditable,
    resizable: isEditable,
  };
}

export function mapServiceProviderCalendarEvents(
  events: ServiceProviderCalendarEventInputModel[]
): EventInput[] {
  return events
    .map(mapServiceProviderCalendarEvent)
    .filter((mapped): mapped is EventInput => mapped !== null);
}
